import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subscription, Observable, of } from 'rxjs';

import { environment } from '../../environments/environment';
import { AuthModel } from '../modules/auth/models/auth.model';

const API_USERS_URL = `${environment.apiUrl}`;

@Injectable({
  providedIn: 'root',
})
export class PagesService {

  // private fields
  private unsubscribe: Subscription[] = [];
  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  // public fields
  httpHeaders: HttpHeaders | undefined;

  constructor(private http: HttpClient) {
    this.httpHeaders = this.getHeaders();
  }

  // transactions
  getTransactions(
    type: string,
    status: string,
    by: string,
    page: number
  ): Observable<any> {
    return this.http.get<any>(
      `${API_USERS_URL}/transactions?page=${page}&type=${type}&status=${status}&by=${by}`,
      {
        headers: this.httpHeaders,
      }
    );
  }

  getTransaction(slug: string): Observable<any> {
    return this.http.get<any>(`${API_USERS_URL}/transactions/info-of/${slug}`, {
      headers: this.httpHeaders,
    });
  }

  // settings
  getSettings(): Observable<any> {
    return this.http.get<any>(`${API_USERS_URL}/settings`, {
      headers: this.httpHeaders,
    });
  }

  // targets
  getTargets(): Observable<any> {
    return this.http.get<any>(`${API_USERS_URL}/targets/get-all`, {
      headers: this.httpHeaders,
    });
  }

  getTarget(slug: string): Observable<any> {
    return this.http.get<any>(`${API_USERS_URL}/targets/show-this/${slug}`, {
      headers: this.httpHeaders,
    });
  }

  // lists
  getCountries(): Observable<any> {
    return this.http.get<any>(`${API_USERS_URL}/countries`, {
      headers: { 'accept-language': 'ar' },
    });
  }

  getCities(): Observable<any> {
    return this.http.get<any>(`${API_USERS_URL}/cities`, {
      headers: { 'accept-language': 'ar' },
    });
  }

  // accept/reject requests
  acceptTransaction(slug: string): Observable<any> {
    return this.http.post<any>(
      `${API_USERS_URL}/transactions/accept/${slug}`,
      null,
      {
        headers: this.httpHeaders,
      }
    );
  }

  rejectTransaction(slug: string): Observable<any> {
    return this.http.post<any>(
      `${API_USERS_URL}/transactions/reject/${slug}`,
      null,
      {
        headers: this.httpHeaders,
      }
    );
  }

  // update/add functions
  addTransaction(formData: FormData): Observable<any> {
    return this.http.post<any>(
      `${API_USERS_URL}/transactions/add/balance`,
      formData,
      { headers: this.httpHeaders }
    );
  }

  refundMoney(formData: FormData): Observable<any> {
    return this.http.post<any>(
      `${API_USERS_URL}/transactions/refund-back/money`,
      formData,
      { headers: this.httpHeaders }
    );
  }

  addTarget(formData: FormData): Observable<any> {
    return this.http.post<any>(`${API_USERS_URL}/targets`, formData, {
      headers: this.httpHeaders,
    });
  }

  setUserInfo(formData: FormData, slug: string): Observable<any> {
    return this.http.post<any>(
      `${API_USERS_URL}/admin/set-personal-info/${slug}`,
      formData,
      {
        headers: this.httpHeaders,
      }
    );
  }

  editSettings(value: number, slug: string): Observable<any> {
    return this.http.post<any>(
      `${API_USERS_URL}/settings/update-this/${slug}`,
      {
        value: value,
      },
      { headers: this.httpHeaders }
    );
  }

  editTarget(slug: string, formData: FormData): Observable<any> {
    return this.http.post<any>(
      `${API_USERS_URL}/targets/update/${slug}`,
      formData,
      { headers: this.httpHeaders }
    );
  }

  switchStatus(slug: string): Observable<any> {
    return this.http.post<any>(
      `${API_USERS_URL}/targets/switch-status/${slug}`,
      null,
      { headers: this.httpHeaders }
    );
  }

  updateHttpHeader() {
   this.httpHeaders = this.getHeaders()
  }

  // private methods
  private getAuthFromLocalStorage(): AuthModel | undefined {
    try {
      const lsValue = localStorage.getItem(this.authLocalStorageToken);
      if (!lsValue) {
        return undefined;
      }
      const authData = JSON.parse(lsValue);
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  private getHeaders(): HttpHeaders | undefined {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.authToken) {
      return undefined;
    }
    return new HttpHeaders({
      Authorization: `Bearer ${auth.authToken}`,
      Accept: 'application/json',
    });
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
